import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phoenix Hatch 🐦‍🔥 — Premium daily reveal.
 * Tap the egg → 3 quick shakes → cracks open → prize reveal.
 * Backend: POST /api/hatch/open (1/day, Premium-only).
 *
 * Free users see a teaser CTA pointing to Premium.
 */
export default function PhoenixHatch() {
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | shaking | revealing | done
  const [prize, setPrize] = useState(null);
  const safety = useRef(null);

  async function loadState() {
    try {
      const { data } = await axios.get(`${API}/hatch/state`, { withCredentials: true });
      setState(data);
    } catch {
      setState(null);
    }
  }

  useEffect(() => {
    if (user) loadState();
  }, [user]);

  useEffect(() => () => {
    if (safety.current) clearTimeout(safety.current);
  }, []);

  // Deep-link handler: when the user lands on /profile?action=hatch (from the
  // email reminder), auto-scroll to the Phoenix Hatch card and auto-click
  // the egg if a hatch is available today. Fires once per page load.
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (autoTriggered.current || !state) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") !== "hatch") return;
    autoTriggered.current = true;
    setTimeout(() => {
      const el = document.querySelector('[data-testid="phoenix-hatch"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Auto-open the egg if available; otherwise just scroll.
      if (state.is_premium && state.available_today) {
        setTimeout(() => openEgg(), 700);
      }
      // Clean the URL so a refresh doesn't re-trigger.
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!user || !state) return null;

  const premium = !!state.is_premium;
  const available = !!state.available_today;
  const hatched = !!state.hatched_today;

  async function openEgg() {
    if (busy) return;
    if (!premium) {
      toast.error("Phoenix Hatch is a Premium daily reveal.", {
        description: "Unlock Premium to claim one each day.",
      });
      return;
    }
    if (!available) {
      toast.info("You already hatched today. Come back tomorrow 🐦‍🔥");
      return;
    }
    setBusy(true);
    setPrize(null);
    setPhase("shaking");
    // Safety: no matter what, bail out after 9s
    if (safety.current) clearTimeout(safety.current);
    safety.current = setTimeout(() => {
      setBusy(false);
      setPhase("idle");
    }, 9000);
    try {
      const { data } = await axios.post(`${API}/hatch/open`, {}, { withCredentials: true });
      // Let the shake animation play (1.6s) before showing prize
      setTimeout(() => {
        setPhase("revealing");
        setPrize(data.drop);
        // After reveal, mark done
        setTimeout(async () => {
          setPhase("done");
          // Prize toast
          const d = data.drop;
          const isMega = !!data.guaranteed_mega;
          if (isMega) {
            toast.success(`🐦‍🔥 STREAK BONUS — MEGA JACKPOT +${d.amount} Phoenix Tokens!`, {
              description: `7-day Hatch streak paid off. Streak: ${data.hatch_streak?.current}d 🔥`,
              duration: 8000,
            });
          } else if (d.kind === "pt") {
            toast.success(`🐦‍🔥 +${d.amount} Phoenix Tokens — ${d.label}!`, { duration: 5500 });
          } else if (d.kind === "omni_dollars") {
            toast.success(`☯️ +${d.amount} Omni Dollars!`, {
              description: "Spend in the Shop.",
              duration: 5500,
            });
          } else if (d.kind === "sticker") {
            if (d.duplicate) {
              toast.info(`${d.sticker?.icon} Duplicate ${d.sticker?.name} — sell from Stickers panel.`);
            } else {
              toast.success(`${d.sticker?.icon} New sticker: ${d.sticker?.name}!`, {
                description: `Rarity: ${d.sticker?.rarity}`,
                duration: 5500,
              });
            }
          } else if (d.kind === "perk_drop") {
            toast.success(`⚡ Perk dropped — ${d.name}!`, {
              description: d.description || "Check your Phoenix Lounge inventory.",
              duration: 6500,
            });
          } else if (d.kind === "exp_charge") {
            toast.success(`⚡ ${d.name || "EXP Charge"} dropped!`, {
              description: `+${d.exp_amount || 100} Hero-Class XP, stored in your Bag. Burn it when you need a push.`,
              duration: 6500,
            });
          } else if (d.kind === "soul_key_drop") {
            toast.success(`🗝️ Soul Key dropped!`, {
              description: "A Soul Key has been added to your stash.",
              duration: 6000,
            });
          } else if (d.kind === "friendship_hearts") {
            // 💕 VIP-egg personal drop — hearts go straight into the owner's
            // Heart Jar (not a gift to a friend). The line reads as an
            // intimate reveal: quiet, warm, earned. Total is surfaced so
            // the player feels the jar filling up over time.
            // Feb 2026: range is 1–20 (weighted toward LOWER); hitting the
            // 20-cap is now the real moment.
            const total = (d.hearts_total || 0).toLocaleString();
            const capHit = d.is_max ? " · 🎰 CAP HIT!" : "";
            toast.success(`💕 +${d.amount} Friendship Hearts — cracked from your VIP Egg!${capHit}`, {
              description: `A quiet, warm little payout — straight into your Heart Jar. Lifetime: ${total} 💕.`,
              duration: 7000,
            });
          } else if (d.kind === "mother_phoenix_crystal") {
            // 💎 Feb 2026 — Replaced the "100 Phoenix Tokens" slot. Lv 1-3
            // crystal, random stat. Socket into gear to add that stat;
            // grows stronger with use (Lv 1-12).
            toast.success(`💎 Mother Phoenix Crystal — Lv${d.level} ${d.stat_label}!`, {
              description: `Socket into gear to add ${d.stat_short}. Grows stronger with use (Lv 1–12).`,
              duration: 7500,
            });
          }
          (data.new_badges || []).forEach((b, i) =>
            setTimeout(() => toast.success(`🏅 Badge — ${b}`), 600 + i * 700)
          );
          await refreshUser?.();
          await loadState();
          if (safety.current) clearTimeout(safety.current);
          setBusy(false);
        }, 1100);
      }, 1600);
    } catch (e) {
      if (safety.current) clearTimeout(safety.current);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "premium_required") {
        toast.error(detail.message || "Premium required");
      } else {
        toast.error(typeof detail === "string" ? detail : "Hatch failed.");
      }
      setPhase("idle");
      setBusy(false);
    }
  }

  async function applyShield() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/hatch/shield/apply`, {}, { withCredentials: true });
      toast.success(`🛡️ Streak saved! ${data.message}`, { duration: 6000 });
      await loadState();
      await refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply shield.");
    } finally {
      setBusy(false);
    }
  }

  const eggClass =
    phase === "shaking"
      ? "ph-shake"
      : phase === "revealing"
      ? "ph-crack"
      : "";

  return (
    <div
      id="phoenix-hatch"
      data-testid="phoenix-hatch"
      className="bg-gradient-to-br from-[#1a0d2b] via-[#0F0F0F] to-[#3a0a0a] border-2 border-[#D4AF37]/60 p-6 mt-6 relative overflow-hidden"
    >
      {/* Glow accent */}
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#D4AF37] opacity-10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1.5 mb-1">
          <span>🐦‍🔥</span> Phoenix Hatch
          <span className="ml-1.5 text-[9px] bg-[#D4AF37] text-black px-1.5 py-0.5">PREMIUM</span>
        </div>
        <h2 className="font-display text-3xl text-white mb-1">
          Crack the <span className="text-[#D4AF37]">Egg.</span>
        </h2>
        <p className="text-xs text-neutral-400 mb-5">
          One free reveal per day. Bigger prizes than the Wheel —
          perks, OD, and the elusive Mega Jackpot 🐦‍🔥
        </p>

        {/* Streak Shield offer (single missed day) */}
        {state.shield_offer?.available && (
          <div
            data-testid="hatch-shield-offer"
            className="mb-4 border-2 border-[#3B82F6] bg-[#0c1a35] p-3 flex flex-wrap items-center gap-3"
          >
            <div className="text-3xl leading-none">🛡️</div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#60A5FA] font-bold">
                Streak About to Reset
              </div>
              <div className="text-sm text-white">
                You missed <span className="text-[#D4AF37] font-bold">{state.shield_offer.missed_date}</span>.
                Spend 1 Streak Shield to save your <span className="text-[#D4AF37] font-bold">{state.shield_offer.would_restore_to}-day</span> streak.
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                You own {state.shield_offer.shields_owned} 🛡️
              </div>
            </div>
            <button
              type="button"
              data-testid="hatch-shield-apply"
              onClick={applyShield}
              disabled={busy}
              className="btn-omni btn-red text-xs uppercase tracking-[0.2em] disabled:opacity-40"
            >
              {busy ? "Applying…" : "Use Shield"}
            </button>
          </div>
        )}

        {/* Hatch Streak progress + day-6 hype banner */}
        {state.hatch_streak && (
          <HatchStreakBar
            current={state.hatch_streak.current}
            longest={state.hatch_streak.longest}
            nextValue={state.next_hatch_value}
            nextIsMega={state.next_is_mega}
            daysUntilMega={state.days_until_mega}
            available={available}
            hatched={hatched}
          />
        )}

        {/* Egg + button */}
        <div className="flex flex-col items-center gap-5 my-4">
          <button
            type="button"
            data-testid="hatch-egg"
            onClick={openEgg}
            disabled={busy || !premium || !available}
            aria-label="Phoenix Hatch egg"
            className={`relative w-40 h-52 flex items-center justify-center transition-transform hover:scale-[1.03] disabled:hover:scale-100 disabled:cursor-not-allowed ${eggClass}`}
            style={{
              background:
                "radial-gradient(ellipse at 35% 30%, #FCD34D 0%, #D4AF37 35%, #8B4513 78%, #4a2208 100%)",
              borderRadius: "50% 50% 48% 48% / 60% 60% 40% 40%",
              boxShadow:
                "inset -8px -16px 32px rgba(0,0,0,0.45), inset 8px 12px 24px rgba(255,235,180,0.35), 0 8px 24px rgba(212,175,55,0.25)",
            }}
          >
            {/* Cracks overlay (visible during reveal) */}
            <svg
              viewBox="0 0 160 200"
              className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-300 ${
                phase === "revealing" || phase === "done" ? "opacity-100" : "opacity-0"
              }`}
            >
              <path
                d="M80 30 L70 60 L88 85 L65 105 L82 130 L70 160 L92 185"
                stroke="#0A0A0A"
                strokeWidth="3"
                fill="none"
              />
              <path
                d="M88 85 L110 90 M82 130 L60 138 M70 60 L50 70"
                stroke="#0A0A0A"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            {/* Inside flame on reveal */}
            {(phase === "revealing" || phase === "done") && (
              <div
                data-testid="hatch-flame"
                className="absolute inset-0 flex items-center justify-center text-5xl ph-flame-burst"
              >
                🔥
              </div>
            )}
          </button>

          {/* CTA / status */}
          {!premium ? (
            <a
              href="#plans"
              data-testid="hatch-premium-cta"
              className="btn-omni btn-red text-sm uppercase tracking-[0.2em]"
            >
              🔒 Premium Only · Unlock
            </a>
          ) : !available ? (
            <button
              type="button"
              data-testid="hatch-done"
              disabled
              className="bg-[#0F0F0F] border border-zinc-800 text-neutral-500 px-6 py-3 text-xs uppercase tracking-[0.2em] cursor-not-allowed"
            >
              Hatched Today · Come Back Tomorrow
            </button>
          ) : (
            <button
              type="button"
              data-testid="hatch-button"
              onClick={openEgg}
              disabled={busy}
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-sm px-8 py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy
                ? phase === "revealing"
                  ? "Revealing..."
                  : "Hatching..."
                : "Hatch Now"}
            </button>
          )}
        </div>

        {/* Reveal card */}
        {prize && phase !== "shaking" && (
          <div
            data-testid="hatch-prize"
            className="border-2 border-[#D4AF37] bg-[#0F0F0F] p-4 text-center max-w-sm mx-auto"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              You Hatched
            </div>
            <div className="text-5xl my-2 leading-none">{prize.icon || "🎁"}</div>
            <div className="font-display text-2xl text-white">
              {prize.kind === "pt"
                ? `+${prize.amount} Phoenix Tokens`
                : prize.kind === "omni_dollars"
                ? `+${prize.amount} Omni Dollars`
                : prize.kind === "friendship_hearts"
                ? `+${prize.amount} Friendship Hearts`
                : prize.kind === "mother_phoenix_crystal"
                ? `Lv${prize.level} ${prize.stat_label} Crystal`
                : prize.kind === "sticker"
                ? prize.duplicate
                  ? `Duplicate · ${prize.sticker?.name || prize.label}`
                  : prize.sticker?.name || prize.label
                : prize.name || prize.label}
            </div>
            {/* 💕 Personal VIP-egg tagline for the Hearts drop — quiet
                and warm; also surfaces the Heart Jar's running total. */}
            {prize.kind === "friendship_hearts" && (
              <div className="mt-2 text-[11px] text-pink-200/90 italic leading-snug">
                A quiet, warm little payout — straight into your Heart Jar.
                {typeof prize.hearts_total === "number" && (
                  <div className="mt-1 text-neutral-500 not-italic tracking-widest text-[10px] uppercase">
                    Heart Jar · {prize.hearts_total.toLocaleString()} 💕
                  </div>
                )}
              </div>
            )}
            {/* 💎 Mother Phoenix Crystal tagline — short on how to use it. */}
            {prize.kind === "mother_phoenix_crystal" && (
              <div className="mt-2 text-[11px] text-amber-200/90 italic leading-snug">
                Socket into gear to add {prize.stat_short}. Grows stronger with use (Lv 1–12).
              </div>
            )}
            {prize.description && (
              <p className="text-xs text-neutral-400 mt-2 italic">{prize.description}</p>
            )}
          </div>
        )}

        {/* Prize wall preview */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
            Prize Wall
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            {(state.pool_preview || []).map((p, i) => (
              <div
                key={i}
                className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center"
                title={p.label}
              >
                <div className="text-xl leading-none">{p.icon}</div>
                <div className="text-[9px] uppercase tracking-wider text-neutral-500 mt-1 truncate">
                  {p.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-4 text-center">
          1 free hatch per day · Mega Jackpot 🐦‍🔥 500 Phoenix Tokens · {state.lifetime_hatches || 0} lifetime
        </p>

        {/* 14-day Hatch History timeline */}
        {Array.isArray(state.history) && state.history.length > 0 && (
          <div className="mt-5 pt-5 border-t border-zinc-800/60" data-testid="hatch-history">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2 flex items-center justify-between">
              <span>Last 14 Days</span>
              <span className="text-neutral-600 normal-case tracking-normal text-[10px]">
                {state.history.filter(d => d.hatched).length}/14 hatched
              </span>
            </div>
            <div className="flex gap-1 justify-between" role="list">
              {state.history.map((d) => (
                <div
                  key={d.date}
                  role="listitem"
                  title={`${d.date}${d.hatched ? " — hatched 🐦‍🔥" : " — missed"}${d.is_today ? " (today)" : ""}`}
                  data-testid={`hatch-history-${d.date}`}
                  className={`flex-1 aspect-square flex items-center justify-center text-base transition-all ${
                    d.hatched
                      ? d.is_today
                        ? "bg-[#D4AF37]/20 border-2 border-[#D4AF37]"
                        : "bg-[#4C1D95]/30 border border-[#4C1D95]"
                      : d.is_today
                      ? "bg-transparent border-2 border-[#D4AF37] border-dashed"
                      : "bg-transparent border border-zinc-800"
                  }`}
                  style={{ borderRadius: "40% 40% 50% 50% / 55% 55% 45% 45%" }}
                  aria-label={`${d.date} ${d.hatched ? "hatched" : "missed"}`}
                >
                  <span className={d.hatched ? "" : "opacity-30"}>
                    {d.hatched ? "🐦‍🔥" : "🥚"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Local animation styles */}
      <style>{`
        @keyframes ph-shake {
          0% { transform: translate(0, 0) rotate(0deg); }
          15% { transform: translate(-6px, -2px) rotate(-6deg); }
          30% { transform: translate(7px, -1px) rotate(6deg); }
          45% { transform: translate(-7px, 1px) rotate(-7deg); }
          60% { transform: translate(7px, 0) rotate(7deg); }
          75% { transform: translate(-5px, -2px) rotate(-4deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        .ph-shake { animation: ph-shake 0.5s ease-in-out 3; }
        @keyframes ph-crack {
          0% { transform: scale(1); }
          40% { transform: scale(1.08) rotate(2deg); }
          60% { transform: scale(0.96) rotate(-2deg); filter: brightness(1.4); }
          100% { transform: scale(1.02); filter: brightness(1.1); }
        }
        .ph-crack { animation: ph-crack 0.9s ease-out; }
        @keyframes ph-flame-burst {
          0% { transform: scale(0.4); opacity: 0; }
          40% { transform: scale(1.4); opacity: 1; }
          100% { transform: scale(1.0); opacity: 1; }
        }
        .ph-flame-burst { animation: ph-flame-burst 0.7s ease-out; }
        @keyframes ph-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(212, 175, 55, 0); }
        }
        .ph-pulse { animation: ph-pulse 1.8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

/* ====== Hatch Streak progress bar (7-day countdown to guaranteed Mega Jackpot) ====== */
function HatchStreakBar({ current, longest, nextValue, nextIsMega, daysUntilMega, available, hatched }) {
  // Show 7 segments; fill `current % 7` (or 7 if next is mega and available).
  const filled = nextIsMega && available ? 6 : (current % 7);
  const segments = Array.from({ length: 7 }, (_, i) => i < filled);
  return (
    <div
      data-testid="hatch-streak-bar"
      className={`mb-5 p-3 border ${nextIsMega && available ? "border-[#D4AF37] bg-[#D4AF37]/10 ph-pulse" : "border-zinc-800 bg-[#0A0A0A]"}`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1.5">
          <span>🔥</span> Hatch Streak
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 tabular-nums">
          {current}d · best {longest || 0}d
        </div>
      </div>

      <div className="flex gap-1 mb-2">
        {segments.map((on, i) => {
          const isMegaSlot = i === 6;
          return (
            <div
              key={i}
              data-testid={`hatch-streak-seg-${i}`}
              className={`flex-1 h-3 border ${
                on
                  ? isMegaSlot
                    ? "bg-[#D4AF37] border-[#D4AF37]"
                    : "bg-[#4C1D95] border-[#4C1D95]"
                  : isMegaSlot
                  ? "bg-transparent border-[#D4AF37]/60"
                  : "bg-transparent border-zinc-800"
              }`}
            />
          );
        })}
      </div>

      {nextIsMega && available ? (
        <p data-testid="hatch-mega-banner" className="text-xs text-[#D4AF37] font-bold uppercase tracking-widest text-center">
          🐦‍🔥 Today's hatch = Guaranteed MEGA JACKPOT
        </p>
      ) : nextIsMega && hatched ? (
        <p className="text-[11px] text-neutral-500 text-center italic">
          Mega Jackpot already claimed today. Streak: {current} 🔥
        </p>
      ) : (
        <p className="text-[11px] text-neutral-500 text-center">
          {daysUntilMega === 1 ? (
            <>
              <span className="text-[#D4AF37] font-bold">Hatch tomorrow</span> for the guaranteed Mega Jackpot 🐦‍🔥
            </>
          ) : (
            <>
              {daysUntilMega} more hatch{daysUntilMega === 1 ? "" : "es"} until guaranteed Mega Jackpot 🐦‍🔥
            </>
          )}
        </p>
      )}
    </div>
  );
}
